import { useQuery } from '@tanstack/react-query';
import { MessageSquare, Send } from 'lucide-react';
import { useId, useState } from 'react';
import { api } from '../../services/api';
import type { PublishResult, ReviewSummary } from '../../types/api';
import { Button } from '../ui/Button';
import { Dialog } from '../ui/Dialog';
import { ErrorState, Notice, SkeletonRows } from '../ui/Feedback';

interface Props {
  open: boolean;
  review: ReviewSummary;
  findingIds: string[];
  onClose: () => void;
  onPublish: () => Promise<PublishResult>;
  publishing: boolean;
  publishError: Error | null;
}

/**
 * Shows exactly what will be posted to GitHub and requires an explicit confirmation checkbox.
 * Nothing is sent until the user confirms.
 */
export function PublishDialog({ open, review, findingIds, onClose, onPublish, publishing, publishError }: Props) {
  const [confirmed, setConfirmed] = useState(false);
  const confirmId = useId();
  const preview = useQuery({
    queryKey: ['publish-preview', review.id, [...findingIds].sort()],
    queryFn: () => api.previewComments(review.id, findingIds),
    enabled: open && findingIds.length > 0,
  });
  const isDemo = review.source === 'demo';

  const close = () => {
    setConfirmed(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      size="lg"
      title={`Publish ${findingIds.length} finding${findingIds.length === 1 ? '' : 's'} to GitHub`}
      description={
        <>
          Comments will be posted as a single review on{' '}
          <span className="font-mono">
            {review.owner}/{review.repo}#{review.prNumber}
          </span>{' '}
          at commit <span className="font-mono">{review.headSha.slice(0, 7)}</span>. No code is pushed and nothing is merged.
        </>
      }
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!confirmed || !preview.data}
            loading={publishing}
            icon={<Send className="h-4 w-4" />}
            onClick={() => {
              void onPublish().then(() => setConfirmed(false), () => undefined);
            }}
          >
            {isDemo ? 'Simulate publish' : 'Publish comments'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {isDemo && (
          <Notice tone="warn" title="Demo review">
            This is a sample PR, so nothing will actually be posted. The flow is simulated end to end.
          </Notice>
        )}
        {publishError && (
          <Notice tone="danger" title="Publishing failed">
            {publishError.message}
          </Notice>
        )}
        {preview.isLoading ? (
          <SkeletonRows rows={3} />
        ) : preview.error ? (
          <ErrorState title="Could not build the preview" error={preview.error} onRetry={() => void preview.refetch()} />
        ) : (
          <ol className="space-y-3">
            {preview.data?.comments.map((c) => (
              <li key={c.findingId} className="rounded-md border border-line">
                <div className="flex items-center gap-2 border-b border-line px-3 py-2 text-xs text-muted">
                  <MessageSquare className="h-3.5 w-3.5" aria-hidden />
                  {c.inline ? (
                    <span>
                      Inline comment on <span className="font-mono text-ink">{c.path}:{c.line}</span>
                    </span>
                  ) : (
                    <span>
                      General comment (line not verified) for <span className="font-mono text-ink">{c.path}</span>
                    </span>
                  )}
                </div>
                <pre className="max-h-56 overflow-auto whitespace-pre-wrap p-3 font-mono text-xs leading-relaxed">{c.body}</pre>
              </li>
            ))}
          </ol>
        )}
        <label htmlFor={confirmId} className="flex items-start gap-2.5 rounded-md bg-raised p-3 text-sm">
          <input
            id={confirmId}
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-[rgb(var(--accent))]"
          />
          <span>I reviewed these comments and want to post them to this Pull Request.</span>
        </label>
      </div>
    </Dialog>
  );
}
